/**
 * Standardized Date and Time utilities.
 * Timezone is read dynamically from business settings (stored in localStorage at login).
 * Falls back to 'America/Chicago' if not set.
 */

const LOCALE = 'en-US';

const getTimezone = () => {
  const stored = localStorage.getItem('etpos_business_timezone');
  if (stored) return stored;
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (e) {
    return 'America/Chicago';
  }
};
// console.log('[DateUtils] Module Loaded');

/**
 * Returns a new Date object adjusted to Chicago time for display purposes.
 * Note: Actual Date objects are UTC-based, but this helps in getting 
 * "What day is it in Chicago right now?"
 */
export const getChicagoNow = () => {
  const now = new Date();
  // We use this to get the string representation in Chicago, then we could parse it
  // if we need a Date object that "looks" like Chicago time.
  return now;
};

/**
 * Formats a date to MM/DD/YYYY HH:MM AM/PM in business timezone.
 * For YYYY-MM-DD HH:mm:ss strings from the DB/API, parses directly
 * to avoid timezone-shifting when passing through new Date().
 */
const getDateFormat = () => localStorage.getItem('etpos_date_format') || 'm/d/Y';
const getTimeFormat = () => localStorage.getItem('etpos_time_format') || '12';

const applyDateFormat = (year, month, day, format) => {
  const parts = {
    Y: String(year),
    m: String(month).padStart(2, '0'),
    d: String(day).padStart(2, '0')
  };
  
  if (format === 'd-m-Y') return `${parts.d}-${parts.m}-${parts.Y}`;
  if (format === 'm-d-Y') return `${parts.m}-${parts.d}-${parts.Y}`;
  if (format === 'd/m/Y') return `${parts.d}/${parts.m}/${parts.Y}`;
  return `${parts.m}/${parts.d}/${parts.Y}`; // default to m/d/Y
};

/**
 * Formats a date to MM/DD/YYYY HH:MM AM/PM or configured business settings format in business timezone.
 */
export const formatDateTime = (date) => {
  if (!date) return '';
  const is24Hr = getTimeFormat() === '24';
  const dateFormat = getDateFormat();

  if (typeof date === 'string') {
    // YYYY-MM-DD HH:mm:ss or YYYY-MM-DD HH:mm — parse directly, treat as business timezone.
    const m = date.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/);
    if (m) {
      const [, yyyy, mo, dd, hh, min] = m;
      let h = parseInt(hh, 10);
      let timeStr = '';
      if (is24Hr) {
        timeStr = `${String(h).padStart(2, '0')}:${min}`;
      } else {
        const ampm = h >= 12 ? 'PM' : 'AM';
        h = h % 12 || 12;
        timeStr = `${String(h).padStart(2, '0')}:${min} ${ampm}`;
      }
      const dateStr = applyDateFormat(yyyy, mo, dd, dateFormat);
      return `${dateStr}, ${timeStr}`;
    }
  }
  const d = typeof date === 'string' ? new Date(date) : date;
  const dateStr = formatDate(d);
  const timeStr = formatTime(d);
  return `${dateStr}, ${timeStr}`;
};

/**
 * Formats a date to MM/DD/YYYY or configured format in business timezone.
 */
export const formatDate = (date) => {
  if (!date) return '';
  if (typeof date === 'string') {
    const m = date.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) {
      return applyDateFormat(m[1], m[2], m[3], getDateFormat());
    }
  }
  const d = typeof date === 'string' ? new Date(date) : date;
  
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: getTimezone(),
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    const parts = formatter.formatToParts(d);
    const month = parts.find(p => p.type === 'month').value;
    const day = parts.find(p => p.type === 'day').value;
    const year = parts.find(p => p.type === 'year').value;
    return applyDateFormat(year, month, day, getDateFormat());
  } catch (e) {
    return d.toLocaleDateString(LOCALE, {
      timeZone: getTimezone(),
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });
  }
};

/**
 * Formats a date to HH:MM AM/PM or 24-hour format in business timezone.
 */
export const formatTime = (date) => {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  const is24Hr = getTimeFormat() === '24';
  return d.toLocaleTimeString(LOCALE, {
    timeZone: getTimezone(),
    hour: '2-digit',
    minute: '2-digit',
    hour12: !is24Hr
  });
};

/**
 * Returns "Today" in Chicago timezone as a local Date object (start of day).
 * This ensures that if it's currently May 8th in Chicago, we return a Date 
 * object representing May 8th 00:00:00 in the user's browser local time.
 */
export const getChicagoToday = () => {
  const chicagoStr = new Date().toLocaleString('en-US', { timeZone: getTimezone(), year: 'numeric', month: '2-digit', day: '2-digit' });
  const [mm, dd, yyyy] = chicagoStr.split('/');
  return new Date(yyyy, mm - 1, dd, 0, 0, 0, 0);
};

/**
 * Returns a YYYY-MM-DDTHH:mm string for the provided date.
 * Uses local getters to maintain the 'literal' date/time.
 */
export const getChicagoISOString = (date = new Date()) => {
  const d = typeof date === 'string' ? new Date(date) : date;
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: getTimezone(),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
  
  const parts = formatter.formatToParts(d);
  const getPart = (type) => parts.find(p => p.type === type).value;
  const result = `${getPart('year')}-${getPart('month')}-${getPart('day')}T${getPart('hour')}:${getPart('minute')}`;
  // console.log(`[DateUtils] getChicagoISOString for ${d.toISOString()} -> ${result}`);
  return result;
};

/**
 * Formats a date to YYYY-MM-DD HH:MM:SS for API compatibility.
 * Uses local getters to ensure the date/time being viewed by the user
 * is exactly what is sent to the API.
 */
export const formatDateForApi = (date = new Date()) => {
  if (!date) return '';
  const d = typeof date === 'string' 
    ? (date.includes('T') || date.includes(' ') ? new Date(date) : new Date(date.replace(/-/g, '/'))) 
    : date;

  
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  
  return `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`;
};

/**
 * Formats a date to YYYY-MM-DD 23:59:59 for API compatibility (end of day).
 */
export const formatEndOfDateForApi = (date = new Date()) => {
  if (!date) return '';
  const d = typeof date === 'string' 
    ? (date.includes('T') || date.includes(' ') ? new Date(date) : new Date(date.replace(/-/g, '/'))) 
    : date;

  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  
  return `${yyyy}-${mm}-${dd} 23:59:59`;
};

const parseBusinessDateString = (dateStr) => {
  if (!dateStr) return null;
  // Match date and time parts (supporting - and / as separators)
  const match = dateStr.match(/^(\d{2})[-/](\d{2})[-/](\d{4})[,\s]+(\d{2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?$/i);
  if (!match) return null;

  const [, part1, part2, year, hour, minute, second = '00', ampm] = match;
  
  const format = getDateFormat();
  let day, month;
  if (format.startsWith('d')) {
    day = parseInt(part1, 10);
    month = parseInt(part2, 10) - 1;
  } else {
    month = parseInt(part1, 10) - 1;
    day = parseInt(part2, 10);
  }

  let hh = parseInt(hour, 10);
  if (ampm) {
    if (ampm.toUpperCase() === 'PM' && hh < 12) hh += 12;
    if (ampm.toUpperCase() === 'AM' && hh === 12) hh = 0;
  }

  const pad = (n) => String(n).padStart(2, '0');
  return `${year}-${pad(month + 1)}-${pad(day)} ${pad(hh)}:${minute}:${second}`;
};

/**
 * Formats a date to YYYY-MM-DD HH:MM:SS in UTC for API compatibility.
 * Matches the live software's behavior of using toISOString().
 */
export const formatUTCForApi = (date = new Date()) => {
  if (!date) return '';
  
  if (typeof date === 'string') {
    // 1. Try our business date string parser first
    const parsed = parseBusinessDateString(date);
    if (parsed) return parsed;

    // Case 1: YYYY-MM-DD — append current business timezone (Chicago) time
    if (date.length === 10 && date.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const now = new Date();
      const fmt = new Intl.DateTimeFormat('en-CA', {
        timeZone: getTimezone(),
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
      });
      const parts = fmt.formatToParts(now);
      const getPart = (type) => parts.find(p => p.type === type).value;
      return `${date} ${getPart('hour')}:${getPart('minute')}:${getPart('second')}`;
    }
    
    // Case 2: YYYY-MM-DDTHH:mm or YYYY-MM-DD HH:mm:ss
    if (date.match(/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2})?.*$/)) {
      let normalized = date.replace('T', ' ');
      if (normalized.length === 16) {
        normalized += ':00';
      }
      return normalized.substring(0, 19);
    }
    
    // Case 3: MM/DD/YYYY, HH:mm AM/PM or MM/DD/YYYY HH:mm:ss AM/PM
    const match = date.match(/^(\d{2})\/(\d{2})\/(\d{4})[,\s]+(\d{2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?$/i);
    if (match) {
      const [, month, day, year, hour, minute, second = '00', ampm] = match;
      let hh = parseInt(hour, 10);
      if (ampm) {
        if (ampm.toUpperCase() === 'PM' && hh < 12) hh += 12;
        if (ampm.toUpperCase() === 'AM' && hh === 12) hh = 0;
      }
      const formattedHour = String(hh).padStart(2, '0');
      return `${year}-${month}-${day} ${formattedHour}:${minute}:${second}`;
    }
    
    // Fallback: parse string to Date
    const d = new Date(date);
    if (isNaN(d.getTime())) return date;
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  }

  // If it's a Date object, format it in the business timezone
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: getTimezone(),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
  const parts = formatter.formatToParts(date);
  const getPart = (type) => parts.find(p => p.type === type).value;
  return `${getPart('year')}-${getPart('month')}-${getPart('day')} ${getPart('hour')}:${getPart('minute')}:${getPart('second')}`;
};

/**
 * Returns the current time as YYYY-MM-DD HH:mm:ss in the business timezone (America/Chicago).
 * Use this when stamping new transactions so the timestamp matches the business setting,
 * regardless of the client's local system timezone.
 */
export const nowUTC = () => formatUTCForApi(new Date());

export default {
  formatDateTime,
  formatDate,
  formatTime,
  getChicagoNow,
  getChicagoToday,
  getChicagoISOString,
  formatDateForApi,
  formatEndOfDateForApi,
  formatUTCForApi,
  nowUTC
};
